import React, { useState, useEffect, useMemo, useCallback } from "react";
import { columns } from "../../../constants/userTable";
import BasicCustomTable from "../../../component/basicTable/index";
import UserTopSection from "./userComponent/userTopSection";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUserType, setUserView } from "../../../Redux/Slices/counterSlice";
import axiosInstance from "../../../configuration/request";
import { getUserManagementPermissions } from "../../../utils/rolesAndPermissions";
import { useSnackbar } from "../../../component/SnackbarContext";
import useDebounce from "../../../utils/useDebounce";

function UserManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();

  const [userData, setUserData] = useState([]);
  const [userResponse, setUserResponse] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const permissions = useSelector((state) => state.counter.permissions);
  const rolePermissions = permissions?.[0]?.permissions || [];
  const { canView, canEdit, canCreate } = getUserManagementPermissions(rolePermissions);

  const finalColumns = useMemo(() => {
    if (!canView && !canEdit) {
      return columns.filter((col) => col.field !== "action");
    }
    return columns;
  }, [canView, canEdit]);

  // Centralized fetch function
  const fetchUsers = useCallback((pageNumber = 1, limit = 10, search = "") => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    axiosInstance
      .get(
        `customer/users?usertype=business-delivery&page=${pageNumber}&limit=${limit}&isApproved=approved${searchParam}`
      )
      .then((res) => {
        const formattedUser = res?.data?.data?.map((item) => ({
          id: item?.id || "",
          name: item?.name || "",
          email: item.email || "",
          phone: item?.phone_number || "",
          gender: item?.gender || "",
          birth_date: item?.dob || "",
          role: item?.userType || "-",
          order_status:
            item?.userType === "business user"
              ? item?.stores?.[0]?.storeStatus ? "Active" : "Inactive"
              : item?.user_status ? "Active" : "Inactive",
          preferred:
            item?.userType === "business user" ? !!item?.stores?.[0]?.preferred : "",
          store_id:
            item?.userType === "business user" ? item?.stores?.[0]?.id : "",
        }));
        setUserResponse(res?.data?.data || []);
        setUserData(formattedUser || []);
        setPage(Number(res?.data?.page) || 1);
        setTotalCount(res?.data?.total || 0);
      })
      .catch((error) => {
        showSnackbar(
          error?.response?.data?.message || "Something went wrong!",
          "error"
        );
      });
  }, [showSnackbar]);

  // Effect triggers on search, page change, or rows per page change
  useEffect(() => {
    fetchUsers(page, rowsPerPage, debouncedSearch);
  }, [debouncedSearch, page, rowsPerPage, fetchUsers]);

  const handlePageChangeRequest = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const handleEdit = (row) => {
    dispatch(setUserType({ userType: row.role }));
    navigate(`/app/user-management/edit-user/${row.id}`);
  };

  const handleView = (row) => {
    const fullUserData = userResponse.find((user) => user.id === row.id);
    dispatch(setUserType({ userType: row.role }));
    dispatch(setUserView(fullUserData));
    navigate(`/app/user-management/view-user`);
  };

  const handleSearchFilter = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handlePreferredBy = (value, row) => {
    axiosInstance
      .patch(`/customer/store/${row.store_id}/preferred?value=${value}`)
      .then(() => {
        fetchUsers(page, rowsPerPage, debouncedSearch); // Refetch current page
        showSnackbar("Preference updated successfully", "success");
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || "Update failed", "error");
      });
  };

  const handleStatusChange = (status, rowData) => {
    const isActive = status === "active";
    const isBusiness = rowData?.role === "business user";
    
    const url = isBusiness 
      ? `/customer/store/update-store/${rowData?.store_id}` 
      : `/customer/users/${rowData?.id}`;
    
    const payload = isBusiness 
      ? { storeStatus: isActive } 
      : { user_status: isActive };

    axiosInstance
      .patch(url, payload)
      .then(() => {
        fetchUsers(page, rowsPerPage, debouncedSearch); // Refetch current page
        showSnackbar("Status updated successfully", "success");
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || "Update failed", "error");
      });
  };

  return (
    <div className="px-[24px]">
      <UserTopSection canCreate={canCreate} searchFilter={handleSearchFilter} />
      <BasicCustomTable
        columns={finalColumns}
        rows={userData}
        onEdit={handleEdit}
        onView={handleView}
        colorHeader={true}
        customClassHeader={"table-header-cell-colored"}
        tableName="user-management"
        maxHeightClass="max-h-[calc(100vh-240px)]"
        backendPagination={true}
        totalCount={totalCount}
        onPageChangeRequest={handlePageChangeRequest}
        pageLimit={rowsPerPage}
        page={page}
        canEdit={canEdit}
        canView={canView}
        onSetDefault={handlePreferredBy}
        onTagChange={handleStatusChange}
      />
    </div>
  );
}

export default UserManagement;
